import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const STOPWORDS = new Set([
    "the", "and", "for", "with", "this", "that", "from", "into", "your", "you", "our", "are",
    "senior", "junior", "lead", "principal", "staff", "head", "chief", "manager", "specialist",
    "associate", "intern", "trainee", "executive", "vice", "president", "officer",
    "year", "years", "exp", "experience", "based", "remote", "hybrid", "office", "onsite",
    "full", "time", "part", "contract", "permanent",
]);

function deriveKeywords(title) {
    const tokens = (title || "")
        .toLowerCase()
        .replace(/[^a-z0-9+#./\s-]/g, " ")
        .split(/\s+/)
        .filter(Boolean);
    const out = [];
    for (const t of tokens) {
        if (t.length < 3) continue;
        if (STOPWORDS.has(t)) continue;
        if (out.includes(t)) continue;
        out.push(t);
        if (out.length >= 4) break;
    }
    return out;
}

const toCsv = (a) => (Array.isArray(a) ? a.join(", ") : (a || ""));
const fromCsv = (s) => String(s || "").split(/[,\n]/).map((x) => x.trim()).filter(Boolean);

/**
 * Pops up pre-filled with the source job's data so the recruiter can save it as a reusable seed.
 * Onward, any new job whose title contains the chosen keywords will offer this seed in the
 * /jobs/new "Insert manually / Pass to AI" panel.
 */
export default function SaveAsSeedDialog({ open, onClose, job, onSaved }) {
    const [form, setForm] = useState({ name: "", title_keywords: "", description: "", required_skills: "", nice_to_have_skills: "", must_haves: "", red_flags: "" });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open || !job) return;
        setForm({
            name: `${(job.title || "Untitled").trim()} — standard`,
            title_keywords: deriveKeywords(job.title).join(", "),
            description: job.description || "",
            required_skills: toCsv(job.required_skills),
            nice_to_have_skills: toCsv(job.nice_to_have_skills),
            must_haves: (job.must_haves || []).join("\n"),
            red_flags: (job.red_flags || []).join("\n"),
        });
    }, [open, job]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async () => {
        if (!form.name.trim()) { toast.error("Name is required"); return; }
        setBusy(true);
        try {
            const payload = {
                name: form.name.trim(),
                title_keywords: fromCsv(form.title_keywords),
                description: form.description || "",
                required_skills: fromCsv(form.required_skills),
                nice_to_have_skills: fromCsv(form.nice_to_have_skills),
                must_haves: form.must_haves.split("\n").map((s) => s.trim()).filter(Boolean),
                red_flags: form.red_flags.split("\n").map((s) => s.trim()).filter(Boolean),
                active: true,
            };
            await api.post("/seeds", payload);
            toast.success(`Saved as seed "${payload.name}"`);
            onSaved && onSaved();
            onClose();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="rounded-none max-w-3xl w-[95vw] max-h-[92vh] overflow-y-auto" data-testid="save-as-seed-dialog">
                <DialogHeader>
                    <DialogTitle>Save as JD seed</DialogTitle>
                    <DialogDescription>
                        Turn this job into a reusable org-wide template. Any new job whose title contains one of these keywords will be able to pull in the description + skills + must-haves + red-flags below.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-mono">Seed name *</Label>
                            <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-none h-10 mt-1" data-testid="sas-name" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-mono">Title triggers (CSV; empty = all)</Label>
                            <Input value={form.title_keywords} onChange={(e) => set("title_keywords", e.target.value)} className="rounded-none h-10 mt-1" placeholder="engineer, developer" data-testid="sas-keywords" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-mono">JD copy (rich text or markdown)</Label>
                        <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={5} className="rounded-none mt-1 font-mono text-xs" data-testid="sas-description" />
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-mono">Required skills (CSV)</Label>
                            <Input value={form.required_skills} onChange={(e) => set("required_skills", e.target.value)} className="rounded-none h-10 mt-1" data-testid="sas-required-skills" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-mono">Nice-to-haves (CSV)</Label>
                            <Input value={form.nice_to_have_skills} onChange={(e) => set("nice_to_have_skills", e.target.value)} className="rounded-none h-10 mt-1" data-testid="sas-nice-skills" />
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-mono">Must-haves (1 per line)</Label>
                            <Textarea value={form.must_haves} onChange={(e) => set("must_haves", e.target.value)} rows={3} className="rounded-none mt-1" data-testid="sas-must-haves" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-mono">Red flags (1 per line)</Label>
                            <Textarea value={form.red_flags} onChange={(e) => set("red_flags", e.target.value)} rows={3} className="rounded-none mt-1" data-testid="sas-red-flags" />
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} className="rounded-none">Cancel</Button>
                    <Button onClick={submit} disabled={busy} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="sas-save">{busy ? "Saving…" : "Save as seed →"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
